import type { HttpClient } from '@angular/common/http';
import {
  Observable,
  OperatorFunction,
  catchError,
  forkJoin,
  map,
  of,
  switchMap,
} from 'rxjs';

/**
 * Open PR ids per repo (Bug 34839), read from the bridge's `GET ryn-db/active-prs`, so
 * "Needs you" drops a review task whose PR already merged or was abandoned. On
 * 2026-09-29, 176 of 234 green rows were closed PRs (343 shown vs ~153 real).
 *
 * Failure direction, load-bearing: a repo whose read failed (an old bridge without the
 * route, 503 degraded, offline) is UNKNOWN, and an unknown or truncated repo never drops a
 * row. The card says PR status is unknown instead, so an outage can never hide real work.
 */
export const OPEN_PR_REPOS = ['UPS1_Workspace', 'Ryn_Core'] as const;

export interface OpenPrSet {
  /** Open PR ids, or null when the repo's read failed (unknown). */
  prIds: ReadonlySet<number> | null;
  /** The bridge's page came back full: a missing id may still be open. */
  truncated: boolean;
}

/** Keyed by lower-cased repo name. */
export type OpenPrIndex = ReadonlyMap<string, OpenPrSet>;

/** known = every repo in play was read in full; unknown = none was. */
export type PrStatusState = 'known' | 'partial' | 'unknown';

interface ActivePrsResponse {
  prIds?: unknown;
  possiblyTruncated?: unknown;
}

const ADO_PROJECT = 'UPS1_Workspace';

/** Same repo-name rule as `prHref`: the last path segment, empty means the default. */
export function repoName(repo: string | null | undefined): string {
  return (repo || ADO_PROJECT).split('/').filter(Boolean).pop() ?? ADO_PROJECT;
}

/** `repoName`, case-insensitive. */
export function repoKey(repo: string | null | undefined): string {
  return repoName(repo).toLowerCase();
}

export function toOpenPrSet(r: ActivePrsResponse | null): OpenPrSet {
  if (!r || !Array.isArray(r.prIds)) return { prIds: null, truncated: false };
  const ids = r.prIds.filter((n): n is number => typeof n === 'number');
  return { prIds: new Set(ids), truncated: r.possiblyTruncated === true };
}

/** True only when the repo's full open set is known and the PR is not in it. */
export function isPrClosed(
  index: OpenPrIndex | null,
  repo: string | null | undefined,
  prId: number
): boolean {
  const set = index?.get(repoKey(repo));
  return !!set && set.prIds !== null && !set.truncated && !set.prIds.has(prId);
}

/** Whether the PR rows on the card are reconciled, over the repos they come from. */
export function prStatusState(
  index: OpenPrIndex | null,
  repos: readonly (string | null | undefined)[]
): PrStatusState {
  const keys = [...new Set(repos.map(repoKey))];
  if (keys.length === 0) return index ? 'known' : 'unknown';
  const known = keys.filter((k) => {
    const s = index?.get(k);
    return !!s && s.prIds !== null && !s.truncated;
  }).length;
  if (known === keys.length) return 'known';
  return known === 0 ? 'unknown' : 'partial';
}

/**
 * The repos to read: the defaults plus every distinct repo the rows come from, one per
 * `repoKey`, as its last path segment (10-10: rows from Employee_Packet and
 * AzureFileProvider were never read, so the card sat at "partial" for good).
 */
export function openPrReposFor(
  rows: readonly { repo: string | null | undefined }[]
): string[] {
  const byKey = new Map<string, string>();
  for (const repo of [...OPEN_PR_REPOS, ...rows.map((r) => r.repo)]) {
    const key = repoKey(repo);
    if (byKey.has(key)) continue;
    byKey.set(key, repoName(repo));
  }
  return [...byKey.values()];
}

/** One read per repo; never errors (a failed repo is unknown). */
export function loadOpenPrIndex(
  http: HttpClient,
  base: string,
  repos: readonly string[] = OPEN_PR_REPOS
): Observable<OpenPrIndex> {
  if (repos.length === 0) return of(new Map());
  return forkJoin(
    repos.map((repo) =>
      http
        .get<ActivePrsResponse>(
          `${base}/ryn-db/active-prs?repo=${encodeURIComponent(repo)}`
        )
        .pipe(
          catchError(() => of(null)),
          map((r) => [repoKey(repo), toOpenPrSet(r)] as const)
        )
    )
  ).pipe(map((entries) => new Map(entries)));
}

/**
 * Bug 34839: pairs the review rows with PR status for every repo they come from
 * (`openPrReposFor`), so a row outside the defaults can still reconcile to "known".
 */
export function withOpenPrs<T extends { repo: string | null | undefined }>(
  http: HttpClient,
  base: string
): OperatorFunction<T[], { rows: T[]; open: OpenPrIndex }> {
  return switchMap((rows) =>
    loadOpenPrIndex(http, base, openPrReposFor(rows)).pipe(
      map((open) => ({ rows, open }))
    )
  );
}
